/**
 * Tracks item heights for a virtual scroll list.
 *
 * Each height is keyed by item identity, not by index. Paging and sorting change which item sits at
 * a given index without changing the data length, so an index-keyed height would end up on the
 * wrong item.
 *
 * Callers pass the item count in rather than let it come from the key list. The keys arrive from the
 * data pipeline and the count comes from the viewport, so the two disagree for one frame whenever
 * the data set changes.
 */
export class ListGeometry {
  private keys: string[] = [];
  private readonly heights = new Map<string, number>();
  private measuredTotal = 0;

  /** Height used for unmeasured items. The mean of real measurements replaces it once any exist. */
  private fallbackItemSize: number;

  constructor(defaultItemSize: number) {
    this.fallbackItemSize = defaultItemSize;
  }

  setKeys(keys: string[]): void {
    this.keys = keys;
  }

  /** Records a height. Returns true if the value changed. */
  measure(key: string, height: number): boolean {
    const previous = this.heights.get(key);
    if (previous === height) return false;
    this.heights.set(key, height);
    this.measuredTotal += height - (previous ?? 0);
    return true;
  }

  /**
   * Discards every measurement and keeps the current mean as the estimate.
   *
   * The mean times the count equals the sum, so the total height does not move. Falling back to the
   * build-time default would move it for the frame or two before fresh measurements arrive, which
   * shows on screen as the scroll spacer jumping.
   */
  invalidate(): void {
    this.fallbackItemSize = this._estimatedItemSize();
    this.heights.clear();
    this.measuredTotal = 0;
  }

  heightAt(index: number): number {
    const key = this.keys[index];
    return (key === undefined ? undefined : this.heights.get(key)) ?? this._estimatedItemSize();
  }

  /** Summed height of the items in `[start, end)`. */
  spanHeight(start: number, end: number): number {
    let total = 0;
    for (let i = start; i < end; i++) total += this.heightAt(i);
    return total;
  }

  offsetAt(index: number): number {
    return this.spanHeight(0, index);
  }

  /**
   * Finds the item at `offset` and returns it with the offset of its own top edge.
   *
   * Clamps to the last item when `offset` sits past the end of the content. That happens briefly
   * when new measurements shrink the total below the current scroll position.
   */
  indexAt(offset: number, itemCount: number): { index: number; offset: number } {
    let runningOffset = 0;
    for (let i = 0; i < itemCount; i++) {
      const height = this.heightAt(i);
      if (runningOffset + height > offset) return { index: i, offset: runningOffset };
      runningOffset += height;
    }
    const index = Math.max(0, itemCount - 1);
    return { index, offset: Math.max(0, runningOffset - this.heightAt(index)) };
  }

  /**
   * Mean of every height measured so far. It converges on the real item height, so unmeasured items
   * do not stay on the build-time guess.
   */
  private _estimatedItemSize(): number {
    return this.heights.size > 0 ? this.measuredTotal / this.heights.size : this.fallbackItemSize;
  }
}
